<template>
  <div class="scenario">
    <div class="scenario__main">
      <div
        class="scenario__chessboard"
        :class="{ 'hide-moves': !getShowMoveTable }"
        :style="boardStyle"
      >
        <Chessboard
          ref="chessboard"
          :orientation="boardOrientation"
          :pgn="currentMove.pgn"
          :initialFen="pgnData.fen"
          :movable="nextColor"
          @onAfterMove="handleBoardMove"
        />
        <BoardControls
          class="board-controls"
          :show-board-size="!isPresentationMode"
          :show-flip="true"
          :show-fen-copy="true"
          :show-pgn-copy="true"
          :show-save="true"
          :show-free-mode="true"
          :show-exit="isCustomScenario"
          :is-presentation-mode="isPresentationMode"
          :fen-value="currentMove.fen || pgnData.fen || ''"
          :pgn-value="
            pgnData.toString({
              includeUserScore: false,
              stopAtMoveId: currentMove.id,
            }) ||
            pgnData.toString({ includeUserScore: false }) ||
            ''
          "
          @size-changed="handleBoardSizeChange"
          @flip="flipBoard"
          @save="$emit('edit', pgnData.toString({ includeUserScore: false }))"
          @free-mode="goToFreeMode"
          @exit="$emit('exit')"
        />
      </div>

      <div
        v-if="pgnData && !pgnData.isPuzzle"
        v-show="getShowMoveTable && !isPresentationMode"
        class="scenario__moves"
        :style="movesTableStyle"
      >
        <ScenarioMovesTable
          :pgnData="pgnData"
          :currentMove="currentMove"
          :isPresentationMode="isPresentationMode"
          @moveSelected="goToMove"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { PgnDataBuilder } from "vue-chess-shared/pgn/PgnDataBuilder";
import Chess from "chess.js";
import Chessboard from "@/components/games/Chessboard";
import ScenarioMovesTable from "@/components/training/moves/ScenarioMovesTable";
import BoardControls from "./BoardControls.vue";
import { boardSizeMixin } from "@/mixins/boardSizeMixin";
import scenarioSyncMixin from "@/mixins/scenarioSyncMixin";

export default {
  name: "ScenarioExample",

  mixins: [boardSizeMixin, scenarioSyncMixin],

  components: {
    BoardControls,
    Chessboard,
    ScenarioMovesTable,
  },

  props: {
    pgn: {
      type: String,
      required: true,
    },

    isPresentationMode: {
      type: Boolean,
      default: false,
    },

    isCustomScenario: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {};
  },

  computed: {
    nextColor() {
      if (this.currentMove && this.currentMove.color) {
        return this.currentMove.color === "b" ? "white" : "black";
      }
      if (this.pgnData && this.pgnData.fen) {
        return this.pgnData.fen.includes(" b ") ? "black" : "white";
      }
      return "white";
    },
  },

  watch: {
    pgn: {
      immediate: true,
      handler(newPgn) {
        if (!newPgn || typeof newPgn !== "string") return;
        this.loadScenario(newPgn);
      },
    },
  },

  methods: {
    resetState() {
      this.currentMove = {};
      this.pgnData = {};
    },

    loadScenario(pgn) {
      if (!pgn || typeof pgn !== "string") return;

      this.resetState();

      try {
        const pgnDataBuilder = new PgnDataBuilder(pgn, new Chess());
        this.pgnData = pgnDataBuilder.pgnData();
        this.goToMove({});
        this.determineAndSetBoardOrientation();
      } catch (error) {
        console.error("Error loadScenario:", error);
        this.$buefy.toast.open({
          message: "Failed to load example: Invalid PGN format",
          type: "is-danger",
        });
      }
    },

    determineAndSetBoardOrientation() {
      if (!this.pgnData) return;

      // Default to white
      let sideToMove = "white";

      // If it is not the initial chess position, show the board from the side to move
      if (this.pgnData.fen) {
        sideToMove = this.pgnData.fen.includes(" b ") ? "black" : "white";
      }

      if (this.boardOrientation !== sideToMove) {
        this.$store.dispatch("updateCoachingOrientation", sideToMove);
        this.syncState();
      }
    },

    goToMove(move) {
      if (!move || !move.id) {
        this.currentMove = {};
        return;
      }

      this.currentMove = move;
    },

    handleBoardMove(moveData) {
      const newMove = this.pgnData.addMove(moveData.move, this.currentMove.id);
      if (newMove) {
        newMove.recordUserMove();
        this.currentMove = newMove;
      }
    },

    goToFreeMode() {
      const currentFen = this.currentMove.fen || this.pgnData.fen;
      this.$emit("enterFreeMode", currentFen);
    },
  },
};
</script>

<style scoped>
@import "../../../assets/styles/scenario-board.css";
</style>
